import { useLocation } from '@tanstack/react-router'
import { useCallback, useEffect } from 'react'

import { CHAT_PANEL_OPENED_EVENT } from '@/modules/chat'
import type { Project } from '@/modules/projects'
import {
  useGettingStartedChoices,
  useGettingStartedProject,
  useGettingStartedSteps,
} from './hooks'
import { useOpenProjectStore } from './openProjectStore'
import { loadDoneSteps, persistDoneSteps } from './prefs'
import { screenOfPath, stepForScreen } from './steps'
import { useGettingStartedStore } from './store'

const CREATE_PROJECT_STEP_KEY = 'create-project'

// Where the tour can send the user next, given the projects the app has loaded.
export type TourTarget =
  | { kind: 'ready'; project: Project }
  | { kind: 'no-statements'; project: Project }
  | { kind: 'processing'; project: Project }
  | { kind: 'no-project' }

const slugOfPath = (pathname: string) =>
  pathname.match(/^\/projects\/([^/]+)/)?.[1] ?? null

const waitingTarget = (project: Project): TourTarget =>
  project.numberOfPages === 0
    ? { kind: 'no-statements', project }
    : { kind: 'processing', project }

// The project open on screen comes first, so the tour follows the user's case.
export const useTourTarget = (): TourTarget => {
  const { pathname } = useLocation()
  const openProject = useOpenProjectStore((state) => state.project)
  const listedProject = useGettingStartedProject()
  const currentProject =
    openProject && openProject.slug === slugOfPath(pathname)
      ? openProject
      : null
  const candidates = [currentProject, listedProject].filter(
    (project): project is Project => project !== null,
  )
  const processed = candidates.find((project) => project.hasTransactions)
  if (processed) return { kind: 'ready', project: processed }
  return candidates[0] ? waitingTarget(candidates[0]) : { kind: 'no-project' }
}

export const useMarkStepDone = () => {
  const { userId } = useGettingStartedChoices()
  const setDoneSteps = useGettingStartedStore((state) => state.setDoneSteps)
  return useCallback(
    (stepKey: string) => {
      if (userId === null) return
      // A child screen can report a step before the tour has read storage.
      const { doneSteps, hydratedForUser } = useGettingStartedStore.getState()
      const saved = hydratedForUser === null ? loadDoneSteps(userId) : doneSteps
      if (saved.includes(stepKey)) return
      const next = [...saved, stepKey]
      persistDoneSteps(userId, next)
      setDoneSteps(next)
    },
    [userId, setDoneSteps],
  )
}

// A step is done once the user opens its screen on a processed project, or
// opens the chat panel for the chat step. The first step is done once a
// project has processed statements.
export const useTrackTourProgress = () => {
  const { pathname } = useLocation()
  const steps = useGettingStartedSteps()
  const target = useTourTarget()
  const markStepDone = useMarkStepDone()
  const hydrated = useGettingStartedStore(
    (state) => state.hydratedForUser !== null,
  )
  const projectReady = target.kind === 'ready'

  useEffect(() => {
    if (hydrated && projectReady) markStepDone(CREATE_PROJECT_STEP_KEY)
  }, [hydrated, projectReady, markStepDone])

  useEffect(() => {
    const screen = screenOfPath(pathname)
    const step = screen ? stepForScreen(steps, screen) : null
    if (hydrated && projectReady && step) markStepDone(step.key)
  }, [hydrated, projectReady, pathname, steps, markStepDone])

  useEffect(() => {
    const chatStep = stepForScreen(steps, 'chat')
    if (!chatStep) return
    const markChatDone = () => markStepDone(chatStep.key)
    window.addEventListener(CHAT_PANEL_OPENED_EVENT, markChatDone)
    return () =>
      window.removeEventListener(CHAT_PANEL_OPENED_EVENT, markChatDone)
  }, [steps, markStepDone])
}
